import { Link } from "react-router-dom";
import { BadgeCheck, MousePointerClick, Send, Sparkles, ArrowRight, CalendarDays, MapPin } from "lucide-react";
import { useFetch } from "@/hooks/useFetch";
import { useSite } from "@/context/SiteContext";
import { Seo } from "@/components/site/Seo";
import { CourseCard } from "@/components/site/CourseCard";
import { BookButton } from "@/components/site/Header";
import { FloatingMotifs, FairySil, StarSil, BookSil, NoteSil } from "@/components/site/Motifs";
import { WhatsAppIcon } from "@/components/site/WhatsAppFab";
import { fmtDate, todayTR, waLink } from "@/lib/helpers";

function Hero() {
  return (
    <section className="relative overflow-hidden bg-white" data-testid="home-hero">
      <FloatingMotifs />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-10 pb-16 sm:pt-16 lg:pt-20 lg:pb-24 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <span className="rise inline-flex items-center gap-2 rounded-full bg-bej px-4 py-2 text-sm font-bold text-navy">
            <BadgeCheck className="w-4 h-4" /> M.E.B. onaylı özel sanat akademisi
          </span>
          <h1 className="rise mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-navy leading-[1.05] [animation-delay:80ms]">
            Her notada bir masal,<br className="hidden sm:block" /> her derste <span className="relative inline-block">
              yeni bir sayfa
              <svg viewBox="0 0 300 16" className="absolute -bottom-2 left-0 w-full text-bej-dark" aria-hidden="true">
                <path d="M2 10 Q75 2 150 9 T298 7" stroke="currentColor" strokeWidth="6" fill="none" strokeLinecap="round" />
              </svg>
            </span>.
          </h1>
          <p className="rise mt-7 max-w-xl text-base sm:text-lg text-slate-700 leading-relaxed [animation-delay:160ms]">
            Çocuklar ve yetişkinler için müzik ve sanat kursları. Kursunu seç, tanışma dersi için talep bırak; seni arayıp zamanı birlikte netleştirelim.
          </p>
          <div className="rise mt-9 flex flex-wrap gap-3 [animation-delay:240ms]">
            <BookButton testid="hero-book-btn" className="px-7 py-3.5 text-base" />
            <Link to="/kurslar" data-testid="hero-courses-btn"
              className="inline-flex items-center gap-2 rounded-full bg-bej px-7 py-3.5 font-heading font-bold text-navy transition-colors duration-200 hover:bg-bej-dark">
              Kursları İncele <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
        <div className="lg:col-span-5 relative">
          <div className="absolute -inset-4 sm:-inset-6 rounded-[3rem] bg-bej rotate-3" aria-hidden="true" />
          <img src="/images/hero.jpg" alt="Keman çalan çocuk"
            className="relative w-full aspect-[4/5] object-cover rounded-[2.5rem] shadow-2xl shadow-navy/20" />
          <div className="absolute -left-4 sm:-left-10 bottom-10 rounded-2xl bg-white/90 backdrop-blur-xl px-5 py-4 shadow-xl border border-bej flex items-center gap-3">
            <FairySil className="w-10 h-10 text-navy" />
            <div>
              <p className="font-heading font-extrabold text-navy">Tanışma dersi</p>
              <p className="text-xs text-slate-600">Önce tanışalım, sonra başlayalım</p>
            </div>
          </div>
          <StarSil className="motif-twinkle absolute -top-6 right-6 w-8 text-navy" />
        </div>
      </div>
    </section>
  );
}

const STEPS = [
  { icon: MousePointerClick, title: "Kursu Seç", text: "Yaşına ve ilgine uygun kursu incele." },
  { icon: Send, title: "Talep Et", text: "Tarih ve saat seç, iki adımda talebini bırak." },
  { icon: Sparkles, title: "Tanışma Dersine Gel", text: "Onay mesajını al, akademide tanışalım." },
];

function StepsStrip() {
  return (
    <section className="relative bg-bej paper-grain" data-testid="steps-strip">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 grid gap-8 md:grid-cols-3">
        {STEPS.map((s, i) => (
          <div key={s.title} className="relative flex items-start gap-4" data-testid={`step-${i + 1}`}>
            <div className="relative shrink-0 grid place-items-center w-14 h-14 rounded-2xl bg-navy text-white shadow-lg shadow-navy/20">
              <s.icon className="w-6 h-6" />
              <span className="absolute -top-2 -right-2 grid place-items-center w-6 h-6 rounded-full bg-white text-navy text-xs font-black">{i + 1}</span>
            </div>
            <div>
              <h3 className="font-heading text-lg font-extrabold text-navy">{s.title}</h3>
              <p className="text-sm text-navy/80 mt-1">{s.text}</p>
            </div>
            {i < 2 && <div className="hidden md:block absolute top-7 left-[calc(100%-2rem)] w-12 border-t-2 border-dashed border-navy/30" aria-hidden="true" />}
          </div>
        ))}
      </div>
    </section>
  );
}

function Featured() {
  const { data } = useFetch("/courses?featured=true");
  const { categories } = useSite();
  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 sm:py-24" data-testid="featured-courses">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
        <div>
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-navy/60">Öne çıkan kurslar</p>
          <h2 className="mt-2 font-heading text-3xl sm:text-4xl font-black text-navy">Hangi masalı yazmak istersin?</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          {categories.map((c) => (
            <Link key={c.id} to={`/kurslar?kategori=${c.slug}`} data-testid={`home-category-${c.slug}`}
              className="rounded-full border border-navy/20 px-4 py-2 text-sm font-bold text-navy transition-colors hover:bg-navy hover:text-white">
              {c.name}
            </Link>
          ))}
          <Link to="/kurslar" data-testid="home-all-courses" className="rounded-full bg-navy px-4 py-2 text-sm font-bold text-white hover:bg-navy-dark">Tüm kurslar</Link>
        </div>
      </div>
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {(data || []).map((c, i) => <CourseCard key={c.id} course={c} index={i} />)}
      </div>
    </section>
  );
}

function UpcomingEvents() {
  const { data } = useFetch("/events");
  const upcoming = (data || []).filter((e) => e.date >= todayTR()).slice(0, 2);
  if (!upcoming.length) return null;
  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-20" data-testid="home-events">
      <div className="grid lg:grid-cols-12 gap-8 items-start">
        <div className="lg:col-span-4">
          <BookSil className="w-16 text-navy mb-4" />
          <h2 className="font-heading text-3xl font-black text-navy">Sahnede buluşalım</h2>
          <p className="mt-3 text-slate-600">Öğrencilerimizin dinletileri ve konserleri.</p>
          <Link to="/etkinlikler" className="mt-5 inline-flex items-center gap-2 font-bold text-navy hover:gap-3 transition-[gap]" data-testid="home-events-link">
            Tüm etkinlikler <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="lg:col-span-8 grid sm:grid-cols-2 gap-6">
          {upcoming.map((e) => (
            <article key={e.id} className="rounded-3xl overflow-hidden border border-bej bg-white">
              {e.image_url && <img src={e.image_url} alt={e.title} className="w-full aspect-[16/9] object-cover" loading="lazy" />}
              <div className="p-6">
                <p className="flex items-center gap-2 text-sm font-bold text-navy/70"><CalendarDays className="w-4 h-4" />{fmtDate(e.date)} {e.time}</p>
                <h3 className="mt-2 font-heading text-xl font-extrabold text-navy">{e.title}</h3>
                {e.location && <p className="mt-1 flex items-center gap-2 text-sm text-slate-600"><MapPin className="w-4 h-4" />{e.location}</p>}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function CtaBand() {
  const { site } = useSite();
  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="relative overflow-hidden rounded-[2.5rem] bg-navy px-6 py-14 sm:px-14 text-white">
        <NoteSil className="absolute right-10 top-6 w-16 text-white/10 motif-float" />
        <FairySil className="absolute right-[18%] bottom-4 w-24 text-white/10" />
        <h2 className="relative font-heading text-3xl sm:text-4xl font-black max-w-xl">Tanışma dersi için yerini ayırt.</h2>
        <p className="relative mt-3 max-w-lg text-white/80">Hesap açmana gerek yok. Talebini bırak, sana talep numaranla dönüş yapalım.</p>
        <div className="relative mt-8 flex flex-wrap gap-3">
          <Link to="/randevu" data-testid="cta-book-btn" className="rounded-full bg-bej px-7 py-3.5 font-heading font-bold text-navy hover:bg-white transition-colors">Randevu Al</Link>
          {site?.whatsapp_number && (
            <a href={waLink(site.whatsapp_number, "Merhaba, kurslarınız hakkında bilgi almak istiyorum.")} target="_blank" rel="noreferrer"
              data-testid="cta-whatsapp-btn" className="inline-flex items-center gap-2 rounded-full border border-white/30 px-7 py-3.5 font-heading font-bold hover:bg-white/10 transition-colors">
              <WhatsAppIcon className="w-5 h-5" /> WhatsApp'tan yaz
            </a>
          )}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const { site } = useSite();
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": ["EducationalOrganization", "LocalBusiness"],
    name: "Masal Sanat Akademi",
    description: site?.tagline,
    telephone: site?.phone,
    address: site?.address,
    url: window.location.origin,
  };
  return (
    <>
      <Seo description="M.E.B. onaylı Masal Sanat Akademi: piyano, keman, çello, gitar, şan ve çocuklar için müzik kursları. Tanışma dersi için hemen randevu talebi bırakın." jsonLd={jsonLd} />
      <Hero />
      <StepsStrip />
      <Featured />
      <UpcomingEvents />
      <CtaBand />
    </>
  );
}
